import {ExponentialBackoff} from 'simple-backoff';
import {ExtendableError} from 'ts-error';
import {wait} from './wait.js';

export class RetryLimitError extends ExtendableError {}

export interface RetryOptions {
  initialDelay?: number;
  maxDelay?: number;
  delayMultiplier?: number;
  jitter?: number; // between 0 and 1 inclusive
  maxRetries?: number;
}

type StatusCodeError = Error & {statusCode?: number; code?: string};

export async function callWithRetries<T>(
  fn: () => Promise<T>,
  retryCondition: (err: StatusCodeError) => boolean,
  onBeforeRetry: (err: StatusCodeError, retry: number) => void,
  options: RetryOptions
): Promise<T> {
  let numRetries = 0;
  // The backoff strategy could be parameterized in the future if we need more flexibility.
  const backoff = new ExponentialBackoff({
    min: options.initialDelay ?? 100,
    max: options.maxDelay ?? 1000,
    factor: options.delayMultiplier ?? 2,
    jitter: options.jitter ?? 0,
  });

  do {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await fn();
      return result;
    } catch (err) {
      if (!(err instanceof Error)) {
        throw err;
      }

      const shouldRetry: boolean = retryCondition(err);
      if (shouldRetry) {
        const nextNumRetries = numRetries + 1;
        onBeforeRetry(err, nextNumRetries);
        // Wait
        // eslint-disable-next-line no-await-in-loop
        await wait(backoff.next());

        numRetries = nextNumRetries;
      } else {
        throw err;
      }
    }
  } while (numRetries <= (options.maxRetries ?? 5));
  throw new RetryLimitError(`Exceeded retry limit (${options.maxRetries ?? 5})`);
}

export async function retryUntil<T>(
  fn: () => Promise<T>,
  isDone: (val: T) => boolean,
  options: RetryOptions = {}
): Promise<T> {
  let numRetries = 0;

  const backoff = new ExponentialBackoff({
    min: options.initialDelay ?? 100,
    max: options.maxDelay ?? 1000,
    factor: options.delayMultiplier ?? 2,
    jitter: options.jitter ?? 0,
  });

  do {
    // eslint-disable-next-line no-await-in-loop
    const result = await fn();

    const conditionMet: boolean = isDone(result);

    if (conditionMet) {
      return result;
    }

    await wait(backoff.next());

    numRetries += 1;
  } while (numRetries <= (options.maxRetries ?? 5));
  throw new RetryLimitError(`Exceeded retry limit (${options.maxRetries ?? 5})`);
}
